<!-- eslint-disable max-lines -->
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import MeService from '@/core/shared/me/me.service';
import SearchSvg from '@/assets/svg/search-20.svg?component';

import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import CandidateList from '../CandidateList/CandidateList.vue';
import Divider from '@/components/Shared/Divider/Divider.vue';

import { ErrorService } from '@/core/shared/errors/error.service';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import type { ConversationIndex } from '@/core/conversations/conversation-index/conversation-index.type';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import { FeatureName } from '@factoryfixinc/ats-interfaces';
import { MarketingReportType } from '@/components/LaborMarket/labor-market-report-options';

const conversationIndexService = new ConversationIndexService();
const dialogService = new DialogService();
const featureGatingService = new EmployerFeatureGatingService();
const subscriptionService = SubscriptionService.instance;

const selectedProIds = ref<number[]>([]);

const canGenerateMarketingReports = computed(
  () =>
    featureGatingService.hasAccess(FeatureName.MARKETABLE_CANDIDATE_REPORTS) &&
    subscriptionService.isEmployerSubscribed,
);

const selectedRows = computed(() =>
  conversationIndexes.value.filter((row) => selectedProIds.value.includes(row.proId)),
);

function subtitleFor(row: ConversationIndex): string | undefined {
  return [row.lastProJobTitle, row.lastProEmployerName].filter(Boolean).join(' • ') || undefined;
}

function toggleSelect(proId: number) {
  selectedProIds.value = selectedProIds.value.includes(proId)
    ? selectedProIds.value.filter((id) => id !== proId)
    : [...selectedProIds.value, proId];
}

function openMarketingReports() {
  dialogService.openLaborMarketReports({
    defaultType: MarketingReportType.JOB_MATCH,
    candidates: selectedRows.value.map((row) => {
      const subtitle = subtitleFor(row);
      return {
        userProfileId: row.proId,
        name: row.proName,
        ...(subtitle ? { subtitle } : {}),
      };
    }),
  });
}

const conversationIndexes = ref<ConversationIndex[]>([]);
function onDoNotContactRemoved(employerId: number, userProfileId: number) {
  if (new MeService().employer?.id !== employerId) return;
  for (const row of conversationIndexes.value) {
    if (row.proId === userProfileId) row.doNotContact = false;
  }
}

const candidateCount = ref(0);
const searchText = ref('');
const isSearching = ref(false);
const hasSearchedCandidates = ref(false);

const isSearchButtonDisabled = computed(() => {
  return isSearching.value || searchText.value === '';
});

const candidateCountText = computed(() => {
  let text = '';

  const isMoreThanOne = candidateCount.value > 1;
  const hasZeroResults = candidateCount.value === 0;
  const candidateText = isMoreThanOne || hasZeroResults ? 'candidates' : 'candidate';

  if (!hasSearchedCandidates.value) {
    text = `${candidateText} across all jobs`;
  } else {
    text = `matching ${candidateText} found`;
  }

  if (candidateCount.value > 100) {
    text += ` (showing the top 100)`;
  }

  return text;
});

const refineCandidateSearchInputText = computed(() => {
  let text = '';

  const hasSearchedWithNoResults = hasSearchedCandidates.value && candidateCount.value === 0;
  const hasHundredPlusResults = candidateCount.value > 100;

  if (hasSearchedWithNoResults || hasHundredPlusResults) {
    text = `Didn't find what you were looking for? You might want to refine your search.`;
  }

  return text;
});

const isClearButtonDisabled = computed(() => {
  return searchText.value === '';
});

onMounted(() => {
  if (conversationIndexService.lastCandidateArchiveSearch) {
    searchText.value = conversationIndexService.lastCandidateArchiveSearch;
    hasSearchedCandidates.value = true;
  }
  searchCandidates(true);
});

async function handleClearText() {
  if (!hasSearchedCandidates.value) {
    // No search has been performed yet, just clear the text
    searchText.value = '';
  } else {
    // A search has been performed, clear the text and search again
    searchText.value = '';
    await searchCandidates(true);
  }
}

async function searchCandidates(ignoreValidation = false) {
  if (!ignoreValidation && isSearchButtonDisabled.value) {
    return;
  }

  if (!ignoreValidation) {
    hasSearchedCandidates.value = true;
  }

  try {
    isSearching.value = true;

    const data = await conversationIndexService.fetchConversationIndexesByProData(searchText.value);
    conversationIndexes.value = data.headers ?? [];
    selectedProIds.value = [];
    candidateCount.value = data.count ?? 0;
    conversationIndexService.lastCandidateArchiveSearch = searchText.value;
  } catch (error) {
    ErrorService.captureException(error);
  } finally {
    isSearching.value = false;
  }
}
</script>

<template>
  <div class="relative overflow-y-scroll">
    <!-- Search Input -->
    <div class="sticky left-0 top-0 mb-2 w-full">
      <v-text-field
        autofocus
        placeholder="Search candidates across all jobs"
        v-model="searchText"
        class="pro-data-field__input"
        density="compact"
        variant="solo"
        clearable
        persistent-clear
        @keydown.enter="searchCandidates(false)"
      >
        <template v-slot:prepend-inner>
          <SearchSvg class="text-shade-900" />
        </template>
        <template v-slot:append-inner>
          <!-- In-field actions: desktop only. On mobile they crush the input, so
               we hide them here and render a full-width action row below instead. -->
          <span class="hidden items-center md:flex">
            <button
              class="mr-2 cursor-pointer text-sm font-bold"
              :class="{
                'text-shade-500': isClearButtonDisabled,
                'text-black': !isClearButtonDisabled,
              }"
              :disabled="isClearButtonDisabled"
              @click="handleClearText"
            >
              Clear
            </button>
            <button
              class="cursor-pointer rounded-md px-3 py-1.5 text-sm font-semibold"
              :class="{
                'bg-tint-80 text-tint-400': isSearchButtonDisabled,
                'bg-shade-900 text-tint-0': !isSearchButtonDisabled,
              }"
              :disabled="isSearchButtonDisabled"
              @click="searchCandidates(false)"
            >
              <template v-if="isSearching">
                <SpinnerLoader class="mx-4 h-[18px] w-[18px]" dark></SpinnerLoader>
              </template>
              <template v-else> Search </template>
            </button>
          </span>
        </template>
      </v-text-field>

      <!-- Mobile action row: full-width, 44px touch targets (replaces the in-field buttons) -->
      <div class="mt-2 flex gap-2 md:hidden">
        <button
          class="min-h-[44px] flex-1 rounded-md border text-sm font-bold"
          :class="{
            'border-tint-100 text-shade-500': isClearButtonDisabled,
            'border-shade-700 text-shade-900': !isClearButtonDisabled,
          }"
          :disabled="isClearButtonDisabled"
          @click="handleClearText"
        >
          Clear
        </button>
        <button
          class="flex min-h-[44px] flex-[2] items-center justify-center rounded-md text-sm font-semibold"
          :class="{
            'bg-tint-80 text-tint-400': isSearchButtonDisabled,
            'bg-shade-900 text-tint-0': !isSearchButtonDisabled,
          }"
          :disabled="isSearchButtonDisabled"
          @click="searchCandidates(false)"
        >
          <template v-if="isSearching">
            <SpinnerLoader class="h-[18px] w-[18px]" dark></SpinnerLoader>
          </template>
          <template v-else> Search </template>
        </button>
      </div>
    </div>

    <div class="my-6 flex items-center justify-between">
      <p class="font-sans text-sm text-shade-900">
        <span class="font-bold">{{ candidateCount }}</span>
        <span class="ml-1 font-normal">{{ candidateCountText }}</span>
      </p>

      <button
        v-if="canGenerateMarketingReports && selectedProIds.length > 0"
        data-test="generate-marketing-report"
        class="rounded-md border border-shade-900 bg-tint-0 px-3 py-1.5 text-sm font-semibold text-shade-900"
        @click="openMarketingReports"
      >
        Request Job Match Report ({{ selectedProIds.length }})
      </button>
    </div>

    <div>
      <Divider class="hidden md:block" />
      <!-- Headers (table layout only; hidden on mobile where rows render as cards) -->
      <div class="hidden grid-cols-10 gap-8 px-4 py-2 md:grid">
        <!-- Pro Info -->
        <div class="col-span-3 font-sans text-xs font-bold leading-[18px] text-shade-800">
          <p>Name, info and location</p>
        </div>

        <!-- Recent Work Experience -->
        <div class="col-span-2 font-sans text-xs font-bold leading-[18px] text-shade-800">
          <p>Recent work experience</p>
        </div>

        <!-- Applications -->
        <div class="col-span-3 font-sans text-xs font-bold leading-[18px] text-shade-800">
          <p>Applications</p>
        </div>

        <!-- Last Active -->
        <div
          class="col-span-2 text-right font-sans text-xs font-bold leading-[18px] text-shade-800"
        >
          <p>Last active</p>
        </div>
      </div>

      <Divider class="mb-2" />

      <!-- List -->
      <CandidateList
        :conversationIndexes="conversationIndexes"
        :selectable="canGenerateMarketingReports"
        :selectedProIds="selectedProIds"
        @toggle-select="toggleSelect"
        @do-not-contact-removed="onDoNotContactRemoved"
      />

      <!-- Refine Search -->
      <p class="my-12 text-center text-sm font-normal text-shade-820">
        {{ refineCandidateSearchInputText }}
      </p>
    </div>
  </div>
</template>

<style lang="postcss">
.pro-data-field__input {
  & .v-field {
    @apply box-border border border-solid border-shade-700 pr-1 shadow-none !important;
  }
  & .v-field--variant-solo {
    @apply shadow-none !important;
  }
  & input.v-field__input {
    @apply h-10 min-h-0 bg-white p-0 pl-2 text-sm text-shade-900 placeholder:text-tint-300 placeholder:opacity-100 !important;
  }
  & .v-input__details {
    @apply hidden !important;
  }
}
</style>
